import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Icon, icons } from '../ui.js';

interface Props {
  /** Where the menu opens: below the cog in the sidebar, above it on the phone's
   *  tab bar, which sits at the bottom of the screen. */
  placement: 'down' | 'up';
  /** The sidebar's quiet round button, or one of the tab bar's tabs. */
  variant: 'sidebar' | 'tab';
}

const MENU_W = 272;
const EDGE = 12;   // closest the menu may come to the edge of the window

/** Settings: the things that are about Hestia rather than about the list.
 *
 *  A menu rather than a page, because there is not yet enough here to deserve
 *  one — today it is a way to the wall panel's emulator.
 *
 *  The menu is rendered at the top of the document and placed from the cog's
 *  measured position, not nested beside it. Nested, it sat inside the sidebar's
 *  scrolling region, which clipped it to the sidebar — the cog pressed, and
 *  nothing appeared. It closes on a click outside, on Escape (handing focus
 *  back to the cog), and when the window is resized out from under it. */
export function SettingsMenu({ placement, variant }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Measured before paint, so the menu never flashes in the wrong place.
  useLayoutEffect(() => {
    if (!open || !trigger.current) { setPos(null); return; }
    const r = trigger.current.getBoundingClientRect();
    const maxLeft = window.innerWidth - MENU_W - EDGE;
    if (placement === 'down') {
      // Out from the sidebar, over the page: anchored at the cog's left edge.
      setPos({ top: r.bottom + 8, left: Math.max(EDGE, Math.min(r.left - 6, maxLeft)),
               transformOrigin: 'top left' });
    } else {
      // Up from the tab bar, right-aligned to the tab it came from, and clear of
      // the bar's own padding and shadow so the two read as separate surfaces.
      setPos({ bottom: window.innerHeight - r.top + 22,
               left: Math.max(EDGE, Math.min(r.right + 4 - MENU_W, maxLeft)),
               transformOrigin: 'bottom right' });
    }
  }, [open, placement]);

  useEffect(() => {
    if (!open) return;
    const outside = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!trigger.current?.contains(t) && !menu.current?.contains(t)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    const close = () => setOpen(false);
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  // Focus moves into the menu once it is placed, so the keyboard lands on the
  // first choice rather than staying on a button that has done its job.
  useEffect(() => {
    if (pos) menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [pos]);

  return (
    <>
      <button ref={trigger}
              className={variant === 'tab' ? `tab${open ? ' on' : ''}` : `icon-btn${open ? ' on' : ''}`}
              aria-label="Settings" aria-haspopup="menu" aria-expanded={open} aria-controls={menuId}
              onClick={() => setOpen((o) => !o)}>
        <Icon path={icons.settings} size={variant === 'tab' ? 22 : 19} />
      </button>

      {open && pos && createPortal(
        <div ref={menu} id={menuId} role="menu" aria-label="Settings" className="menu" style={pos}>
          <div className="sec menu-sec">Wall panel</div>
          {/* A full navigation, not a view inside the app: the emulator is its
              own page, served by the API and kept out of the service worker's
              reach, so it always shows the live frame. */}
          <a role="menuitem" className="menu-item" href="/board/emulator">
            <Icon path={icons.panel} size={20} />
            <span className="menu-text">
              Board emulator
              <small>The hallway panel, live, with its buttons</small>
            </span>
            <Icon path={icons.chevronRight} size={16} />
          </a>
        </div>,
        document.body,
      )}
    </>
  );
}
